import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import ConsentController from "@source/api/controllers/consentController";
import nursingHomeStore from "@source/store/nursingHomeStore";
import {ConsentDocument} from "@source/api/models/consentModels";
import DateRangePicker from "@source/components/UI/inputs/dateRangePicker/dateRangePicker";
import SearchInput from "@source/components/UI/inputs/searchInput/searchInput";
import {IFillVariant} from "@source/utils/enums";
import {consentDocumentDetailPath} from "@source/api/models/consentHelpers";
import {
  ConsentDocumentCard,
  ConsentFilterBar,
  ConsentRequestState,
} from "@source/components/consentDocuments";
import {filterAwaitingSignDocuments} from "@source/components/consentDocuments/employeeSigningHelpers";
import styles from "./awaitingSign.module.scss";

export const AwaitingSign = observer(() => {
  const nursingHomeId = nursingHomeStore.getId();
  const [documents, setDocuments] = useState<ConsentDocument[]>([]);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<[Date, Date] | [Date] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const response = await ConsentController.listAwaitingEmployeeSignature();
      if (mountedRef.current && requestId === requestIdRef.current) setDocuments(response);
    } catch (requestError: unknown) {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setError(requestError instanceof Error ? requestError.message : "Unable to load documents awaiting your signature.");
      }
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [load]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchText), 500);
    return () => window.clearTimeout(timeout);
  }, [searchText]);

  const homeDocuments = useMemo(
    () => documents.filter((document) => document.nursingHome?._id === nursingHomeId),
    [documents, nursingHomeId],
  );
  const filteredDocuments = useMemo(
    () => filterAwaitingSignDocuments(homeDocuments, search, dateRange),
    [dateRange, homeDocuments, search],
  );
  const hasFilters = Boolean(searchText || dateRange);
  const clearFilters = () => {
    setSearchText("");
    setSearch("");
    setDateRange(null);
  };

  return <div className={styles.queue}>
    <ConsentFilterBar onClear={hasFilters ? clearFilters : undefined}>
      <SearchInput id="awaiting-consent-search" className={styles.search} value={searchText} onChange={(event) => setSearchText(event.target.value)} placeHolder="Search" fill={IFillVariant.Fill}/>
      <DateRangePicker className={styles.dateRange} id="awaiting-consent-date-range" value={dateRange} onChange={setDateRange} placeholder="Date range" fill={IFillVariant.Fill}/>
    </ConsentFilterBar>
    {loading && !documents.length ? <ConsentRequestState variant="loading" title="Loading documents awaiting your signature"/> : null}
    {error && !documents.length ? <ConsentRequestState variant="error" title="Unable to load documents awaiting your signature" description={error} onRetry={() => void load()}/> : null}
    {!loading && !error && !homeDocuments.length ? <ConsentRequestState variant="empty" title="Nothing awaiting your signature" description="Active documents assigned to you will appear here."/> : null}
    {!loading && !error && homeDocuments.length && !filteredDocuments.length ? <ConsentRequestState variant="empty" title="No matching consent documents" description="Try changing or clearing your filters."/> : null}
    {filteredDocuments.length ? <div className={styles.list}>{filteredDocuments.map((document) => (
      <ConsentDocumentCard
        key={document._id}
        document={document}
        link={consentDocumentDetailPath(document._id)}
        employeeSigningCard
        omitEmployeeProgressTotal
      />
    ))}</div> : null}
  </div>;
});
